import { createApi } from '@reduxjs/toolkit/query/react';
import { apiBaseQuery } from 'src/services/apiClient';

export const BULK_USER_IMPORT_API_REDUCER_PATH = 'bulkUserImportApi';
export const sampleFileUrl = '/users/UserUpload/example';
export const pendingFilesUrl = '/users/userupload/upload';
export const createUsersUrl = '/users/userupload/users';
export const editUserRowUrl = '/users/userupload/user/update';
export const refreshUrl = '/users/userupload/user/refresh';
export const deleteUserRowUrl = '/users/userupload/upload/user';
export const uploadFilesUrl = '/users/userupload/upload';
export const tagType = 'UploadedUsers';
export const pendingUsersUrl = (uploadId, page, pageSize, search = '', filter = '') => {
    let url = `${pendingFilesUrl}/users?uploadId=${uploadId}&page=${page}&pageSize=${pageSize}`;
    if (search && search.trim()) {
        // Search parameter will search across firstName, lastName, and email fields
        url += `&search=${encodeURIComponent(search.trim())}`;
    }
    if (filter && filter.trim()) {
        // Filter parameter for advanced filtering (e.g., "Status eq 'Active'")
        url += `&filter=${encodeURIComponent(filter.trim())}`;
    }
    return url;
};
export const createUserFromUploadUrl = uploadId => {
    let url = `${createUsersUrl}?uploadId=${uploadId}&showBranding=false`;
    return url;
};

export const bulkUserImportApi = createApi({
    reducerPath: BULK_USER_IMPORT_API_REDUCER_PATH,
    baseQuery: apiBaseQuery({
        apiVersion: '3.0',
    }),
    tagTypes: [tagType],
    endpoints: builder => ({
        getSampleFile: builder.query({
            query: () => {
                return {
                    url: sampleFileUrl,
                    method: 'GET',
                };
            },
        }),
        getUploadedFiles: builder.query({
            query: () => {
                return {
                    url: pendingFilesUrl,
                    method: 'GET',
                };
            },
        }),
        getUploadedUsers: builder.query({
            query: ({ uploadId, page = 1, pageSize = 25, search = '', filter = '' }) => {
                return {
                    url: pendingUsersUrl(uploadId, page, pageSize, search, filter),
                    method: 'GET',
                };
            },
            providesTags: (result, error, { uploadId }) => [{ type: tagType, id: uploadId }, tagType],
        }),
        createUsersFromUpload: builder.mutation({
            query: ({ uploadId, users = [] }) => {
                const requestConfig = {
                    url: createUserFromUploadUrl(uploadId),
                    method: 'POST',
                    data: users, // Use 'data' instead of 'body' for Axios
                };

                return requestConfig;
            },
            invalidatesTags: (result, error, { uploadId }) => [{ type: tagType, id: uploadId }, tagType],
        }),
        editUserRow: builder.mutation({
            query: ({ newRow }) => {
                return {
                    url: editUserRowUrl,
                    method: 'POST',
                    data: newRow,
                };
            },
            invalidatesTags: (result, error, { uploadId }) => [{ type: tagType, id: uploadId }, tagType],
        }),
        refreshUpdatedUsers: builder.mutation({
            query: ({ id }) => {
                return {
                    url: refreshUrl,
                    method: 'POST',
                    data: id,
                };
            },
        }),
        deleteUserRow: builder.mutation({
            query: ({ id }) => {
                return {
                    url: deleteUserRowUrl,
                    method: 'DELETE',
                    data: [id],
                };
            },
            invalidatesTags: (result, error, { uploadId }) => [{ type: tagType, id: uploadId }, tagType],
        }),
        handleFileDelete: builder.mutation({
            query: ({ uploadId }) => {
                return {
                    url: uploadFilesUrl,
                    method: 'DELETE',
                    data: [uploadId],
                };
            },
            invalidatesTags: (result, error, { uploadId }) => [{ type: tagType, id: uploadId }, tagType],
        }),

        handleFilesUpload: builder.mutation({
            query: ({ payload, contentType }) => {
                return {
                    url: uploadFilesUrl,
                    method: 'POST',
                    headers: {
                        'Content-Type': contentType,
                    },
                    data: payload,
                };
            },
        }),
    }),
});

export const {
    useLazyGetSampleFileQuery,
    useLazyGetUploadedFilesQuery,
    useGetUploadedUsersQuery,
    useCreateUsersFromUploadMutation,
    useEditUserRowMutation,
    useRefreshUpdatedUsersMutation,
    useDeleteUserRowMutation,
    useHandleFilesUploadMutation,
    useHandleFileDeleteMutation,
} = bulkUserImportApi;
